import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@highlight/ui'
import { motion } from 'framer-motion'
import { Check } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { useIsMiniContentHovered } from '../../atoms'
import { trackNotchAction } from '../../track-notch'
import { formatSlot } from '../calendar'
import { SETUP_CALL_TOPICS, useSendSetupCallTopics, type SetupCallBooking } from '../setup-call-api'
import {
  SETUP_CALL_FIELD_ATTRIBUTE,
  SetupCallRow,
  chosenClassName,
  letGoClassName,
  tactileClassName,
  titleClassName,
} from './face'
import { rippleWater } from './water'

/** Left unanswered, the question is skipped after this long. */
const SKIP_MS = 8_000
/** Long enough to read "Sent" before the notch lets go. */
const SENT_HOLD_MS = 600

interface SetupCallBookedProps {
  booking: SetupCallBooking
  zone: string
  onReschedule: () => void
  onDone: () => void
}

const initials = (name: string) =>
  name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

/** Answered or skipped, the question goes and the notch lets go; the call is on their calendar from here. */
export function SetupCallBooked({ booking, zone, onReschedule, onDone }: SetupCallBookedProps) {
  const isHovered = useIsMiniContentHovered()
  const sendTopics = useSendSetupCallTopics()
  const [picks, setPicks] = useState<Array<string>>([])
  const [isAnswering, setAnswering] = useState(false)
  const isSent = sendTopics.isSuccess
  // In flight or sent, what was chosen is what's stored, so the chips and Skip hold still
  const isLocked = isSent || sendTopics.isPending
  const dropRef = useRef<HTMLSpanElement>(null)
  const titleRef = useRef<HTMLHeadingElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)

  // The drop on the check, once as the confirmation lands
  useEffect(() => {
    const drop = dropRef.current
    const ground = drop
      ?.closest<HTMLElement>(`[${SETUP_CALL_FIELD_ATTRIBUTE}]`)
      ?.querySelector<HTMLElement>(`:scope > [${SETUP_CALL_FIELD_ATTRIBUTE}-ground]`)
    const root = ground?.parentElement
    if (!drop || !ground || !root || !titleRef.current || !cardRef.current) return
    const green = getComputedStyle(drop).getPropertyValue('--success').trim()
    return rippleWater(root, ground, drop, [titleRef.current, cardRef.current], { ...BOOKED_WATER, green })
  }, [])

  const skip = useCallback(() => {
    trackNotchAction('setup call', { step: 'topics skipped' })
    onDone()
  }, [onDone])

  // A person who has started answering isn't ignoring the question; once sent, the notch lets go under the pointer too
  const isWaiting = !isSent && (isAnswering || isHovered || sendTopics.isPending)
  useEffect(() => {
    if (isWaiting) return
    const id = window.setTimeout(isSent ? onDone : skip, isSent ? SENT_HOLD_MS : SKIP_MS)
    return () => window.clearTimeout(id)
  }, [isWaiting, isSent, onDone, skip])

  const toggle = (topic: string) => {
    setAnswering(true)
    setPicks((current) => (current.includes(topic) ? current.filter((t) => t !== topic) : [...current, topic]))
  }

  const send = () =>
    sendTopics.mutate(picks, {
      onSuccess: () => {
        setAnswering(false)
        trackNotchAction('setup call', { step: 'topics sent', topics: picks })
      },
      onError: (error) => trackNotchAction('setup call', { step: 'topics failed', error: error.message }),
    })

  return (
    <div className="flex flex-col">
      <div className="flex flex-col items-center pt-3 text-center">
        <span ref={dropRef} className="grid">
          <SetupCallBadge />
        </span>
        <SetupCallRow className="mt-3">
          <h2 ref={titleRef} className={titleClassName}>
            You’re booked
          </h2>
        </SetupCallRow>
        <SetupCallRow className="mt-4 self-stretch">
          <div
            ref={cardRef}
            className="bg-element flex items-center gap-3 rounded-3xl py-2 pl-3 pr-2 text-left [corner-shape:superellipse(1.75)]"
          >
            <span
              aria-hidden
              className="text-foreground-secondary flex size-7 shrink-0 items-center justify-center rounded-full bg-[oklch(1_0_0/0.134)] text-xs font-semibold"
            >
              {initials(booking.hostName)}
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="text-foreground truncate text-sm font-medium tabular-nums">
                {formatSlot(booking.start, zone, ' · ')}
              </span>
              <span className="text-foreground-secondary mt-0.5 text-xs">
                30 min with {booking.hostName}, Highlight
              </span>
            </span>
            <Button
              variant="ghost"
              tone="on-dark"
              size="sm"
              className="shrink-0"
              disabled={sendTopics.isPending}
              onClick={onReschedule}
            >
              Reschedule
            </Button>
          </div>
        </SetupCallRow>
      </div>

      <div
        className="mt-3 flex flex-col gap-2 border-t-[0.5px] border-[oklch(1_0_0/0.071)] pt-3"
        role="group"
        aria-labelledby="setup-call-cover-title"
      >
        <SetupCallRow>
          <p id="setup-call-cover-title" className="text-foreground text-sm font-medium">
            What should we focus on?
          </p>
        </SetupCallRow>
        <div className="flex flex-wrap gap-1.5">
          {SETUP_CALL_TOPICS.map((topic) => (
            <SetupCallRow key={topic}>
              <Button
                variant="secondary"
                size="sm"
                aria-pressed={picks.includes(topic)}
                className={twMerge(picks.includes(topic) && chosenClassName)}
                disabled={isLocked}
                onClick={() => toggle(topic)}
              >
                {topic}
              </Button>
            </SetupCallRow>
          ))}
        </div>
        {sendTopics.isError && <p className="text-foreground-secondary text-xs">Couldn’t send that. Try again.</p>}
        <SetupCallRow className="mt-1 flex gap-2">
          <Button
            size="lg"
            className={tactileClassName}
            disabled={isSent || picks.length === 0}
            loading={sendTopics.isPending}
            onClick={send}
          >
            {isSent ? 'Sent' : 'Send'}
          </Button>
          <Button
            variant="ghost"
            tone="on-dark"
            size="lg"
            className={letGoClassName}
            disabled={isLocked}
            onClick={skip}
          >
            Skip
          </Button>
        </SetupCallRow>
      </div>
    </div>
  )
}

// The water settles into the success field, so it takes that field's black, its wash and the badge's green
const BOOKED_WATER = { ground: 'oklch(0.15 0.008 150)', wash: 'oklch(0.36 0.08 150)' }

const HALO =
  '0 0 20px 2px color-mix(in oklch, var(--success) 35%, transparent), 0 0 56px 12px color-mix(in oklch, var(--success) 12%, transparent)'

/** A green check that lands, with a ring leaving it every two seconds: this just happened. */
function SetupCallBadge() {
  return (
    <motion.span
      aria-hidden
      className="relative grid size-14 place-items-center rounded-full"
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', duration: 0.45, bounce: 0.28, delay: 0.06 }}
    >
      <motion.span
        className="bg-success absolute size-9 rounded-full"
        initial={{ scale: 1, opacity: 0.5 }}
        animate={{ scale: 2.2, opacity: 0 }}
        transition={{ duration: 2, ease: [0.22, 1, 0.36, 1], delay: 0.7, repeat: Infinity }}
      />
      <motion.span
        className="bg-success text-on-success relative grid size-9 place-items-center rounded-full"
        style={{ boxShadow: HALO }}
        initial={{ scale: 0.4 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', duration: 0.45, bounce: 0.28, delay: 0.12 }}
      >
        <Check className="size-4" strokeWidth={2.5} />
      </motion.span>
    </motion.span>
  )
}
